import axios from 'axios';
import { env } from '../config/env';
import { handleAuthExpiry } from '../lib/authRedirect';

// Axios client for the gpustack training gateway. baseURL already includes the
// /api/v1 path segment; endpoints below are appended to it. The JWT (SSO'd from
// the customer portal) is sent as a Bearer token — the backend accepts it.
const api = axios.create({
  baseURL: env.TRAINING_API_URL,
  headers: { 'Content-Type': 'application/json' },
});
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('training_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
api.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error.response?.status === 401) {
      handleAuthExpiry('training_token', '/training');
    }
    return Promise.reject(error);
  }
);

// ---- Types ----------------------------------------------------------------

export interface GpuType {
  gpu_type: string;
  vram_gb: number;
  architecture?: string;
}

export interface PricingRow {
  gpu_type: string;
  gpu_count: number;
  region: string;
  tier: string;
  price_per_hour: number; // already INR
  available: boolean;
  instant_boot: boolean;
}

export type InstanceStatus =
  | 'allocating'
  | 'starting'
  | 'running'
  | 'stopping'
  | 'stopped'
  | 'terminated'
  | 'error';

export interface Instance {
  id: string;
  status: InstanceStatus;
  gpu_type: string;
  gpu_count: number;
  region: string;
  tier: string;
  price_per_hour: number;
  name?: string;
  created_at: string;
  ready_at?: string;
  connection?: string;
  billing_warning?: string;
}

export interface SshKey {
  id: string;
  name: string;
  fingerprint: string;
  created_at: string;
}

// Async operation returned by POST/DELETE /instances. It may already be
// resolved (state=succeeded with resource_id) or still in flight.
export interface Operation {
  operation_id: string;
  kind: string;
  state: string;
  resource_id?: string;
}

export interface CreateInstanceBody {
  gpu_type: string;
  gpu_count: number;
  tier: string;
  region?: string;
  name?: string;
  ssh_key_ids?: string[];
}

export interface Me {
  user_id: string;
  email: string;
}

interface Cursor<T> {
  data: T[];
  next_cursor?: string | null;
}

// ---- Service --------------------------------------------------------------

export interface PricingQuery {
  gpu_type?: string;
  region?: string;
  tier?: string;
}

export const trainingService = {
  getGpuTypes: () => api.get<Cursor<GpuType>>('/gpu-types'),
  getPricing: (params?: PricingQuery) => api.get<Cursor<PricingRow>>('/pricing', { params }),
  // Public — used by the logged-out landing page. /gpu-types and /pricing are
  // public on the gateway, so hit them with a bare axios client (no auth
  // interceptor / 401-redirect).
  getPublicPricing: () =>
    axios.get<Cursor<PricingRow>>(`${env.TRAINING_API_URL.replace(/\/$/, '')}/pricing`),
  getPublicGpuTypes: () =>
    axios.get<Cursor<GpuType>>(`${env.TRAINING_API_URL.replace(/\/$/, '')}/gpu-types`),
  listInstances: () => api.get<Cursor<Instance>>('/instances'),
  createInstance: (body: CreateInstanceBody) => api.post<Operation>('/instances', body),
  deleteInstance: (id: string) => api.delete<Operation>(`/instances/${id}`),
  listSshKeys: () => api.get<{ data: SshKey[] }>('/ssh-keys'),
  createSshKey: (name: string, publicKey: string) =>
    api.post<SshKey>('/ssh-keys', { name, public_key: publicKey }),
  getMe: () => api.get<Me>('/me'),
};
